/**
 * Front-end reveal toggles for offbeat/single-post-meta.
 *
 * Both bubbles are always visible in a fixed cluster on the right. To their
 * left is a single shared track that holds both pill panels (terms and
 * featured vendors) stacked in the same space. Clicking a bubble wipes its
 * panel into the shared track from right to left and closes the other;
 * clicking the active bubble again wipes the track closed. Only one panel is
 * ever shown, and the two data sets occupy the same space.
 *
 * Each vendor pill also springs an editorial hover-card, rendered as a sibling
 * of the clipped track so it floats above everything. Hover-intent (120ms open
 * delay, 80ms close grace, instant warm re-target) keeps it calm while sweeping
 * the row; the card is centered on the pill then clamped to a 12px inset and
 * the pointer slides to stay over the pill. Respects prefers-reduced-motion.
 *
 * Below 767px the swipe track and hover-card are hidden by CSS; the bubbles
 * stay put and tapping one slides a bottom sheet up over a scrim. The gold
 * sheet lists each vendor as a full-width row linking to the profile, the pink
 * sheet is a wrapping cluster of tag links. Tapping the other bubble swaps the
 * sheet contents in place; the scrim, close button, Escape, or re-tapping the
 * active bubble dismisses it.
 */
document.addEventListener( 'DOMContentLoaded', function () {
	var blocks = document.querySelectorAll( '.offbeat-single-post-meta' );
	var duration = 450;
	var mobileMql = window.matchMedia( '(max-width: 767px)' );

	blocks.forEach( function ( block ) {
		var track = block.querySelector( '.offbeat-single-post-meta__track' );
		var toggles = Array.prototype.slice.call(
			block.querySelectorAll( '.offbeat-single-post-meta__toggle' )
		);
		var panels = Array.prototype.slice.call(
			block.querySelectorAll( '.offbeat-single-post-meta__panel' )
		);
		if ( ! track || ! toggles.length || ! panels.length ) {
			return;
		}

		track.style.overflow = 'hidden';
		track.style.clipPath = 'inset(0 0 0 100%)';

		var active = null;
		var frame = null;
		var start = null;
		var fromInset = 100;
		var toInset = 100;
		var currentInset = 100;

		function animate( now ) {
			if ( start === null ) {
				start = now;
			}
			var elapsed = now - start;
			var progress = elapsed / duration;
			if ( progress > 1 ) {
				progress = 1;
			}
			var eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow( -2 * progress + 2, 2 ) / 2;
			currentInset = fromInset + ( toInset - fromInset ) * eased;
			track.style.clipPath = 'inset(0 0 0 ' + currentInset + '%)';
			if ( progress < 1 ) {
				frame = requestAnimationFrame( animate );
			}
		}

		function runWipe( from, to ) {
			fromInset = from;
			toInset = to;
			currentInset = from;
			start = null;
			if ( frame ) {
				cancelAnimationFrame( frame );
			}
			frame = requestAnimationFrame( animate );
		}

		function showPanel( target ) {
			panels.forEach( function ( panel ) {
				panel.classList.toggle( 'is-active', panel.getAttribute( 'data-panel' ) === target );
			} );
			toggles.forEach( function ( toggle ) {
				var on = toggle.getAttribute( 'data-target' ) === target;
				toggle.classList.toggle( 'is-active', on );
				toggle.setAttribute( 'aria-expanded', on ? 'true' : 'false' );
			} );
		}

		function closeAll() {
			panels.forEach( function ( panel ) {
				panel.classList.remove( 'is-active' );
			} );
			toggles.forEach( function ( toggle ) {
				toggle.classList.remove( 'is-active' );
				toggle.setAttribute( 'aria-expanded', 'false' );
			} );
		}

		var sheet = setupSheet( block, toggles );

		toggles.forEach( function ( toggle ) {
			toggle.addEventListener( 'click', function () {
				var target = toggle.getAttribute( 'data-target' );

				if ( mobileMql.matches ) {
					sheet.toggle( toggle, target );
					return;
				}

				if ( active === target ) {
					active = null;
					closeAll();
					runWipe( currentInset, 100 );
					return;
				}

				active = target;
				showPanel( target );
				runWipe( 100, 0 );
			} );
		} );

		setupHoverCard( block );
	} );

	function setupSheet( block, toggles ) {
		var scrim = block.querySelector( '.offbeat-single-post-meta__scrim' );
		var sheet = block.querySelector( '.offbeat-single-post-meta__sheet' );
		var title = block.querySelector( '.offbeat-single-post-meta__sheet-title' );
		var closeBtn = block.querySelector( '.offbeat-single-post-meta__sheet-close' );
		var sheetPanels = Array.prototype.slice.call(
			block.querySelectorAll( '.offbeat-single-post-meta__sheet-panel' )
		);
		var current = null;
		var lastToggle = null;

		if ( ! scrim || ! sheet ) {
			return { toggle: function () {} };
		}

		function open( toggle, target ) {
			current = target;
			lastToggle = toggle;
			sheetPanels.forEach( function ( panel ) {
				panel.hidden = panel.getAttribute( 'data-sheet-panel' ) !== target;
			} );
			title.textContent = toggle.getAttribute( 'data-sheet-title' ) || '';
			sheet.classList.toggle( 'is-terms', target === 'terms' );
			scrim.hidden = false;
			sheet.hidden = false;
			requestAnimationFrame( function () {
				scrim.classList.add( 'is-open' );
				sheet.classList.add( 'is-open' );
			} );
			toggles.forEach( function ( t ) {
				t.setAttribute( 'aria-expanded', t === toggle ? 'true' : 'false' );
			} );
		}

		function close() {
			current = null;
			sheet.style.transform = '';
			sheet.style.transition = '';
			scrim.classList.remove( 'is-open' );
			sheet.classList.remove( 'is-open' );
			toggles.forEach( function ( t ) {
				t.setAttribute( 'aria-expanded', 'false' );
			} );
			var focusBack = lastToggle;
			window.setTimeout( function () {
				scrim.hidden = true;
				sheet.hidden = true;
			}, 240 );
			if ( focusBack ) {
				focusBack.focus();
			}
		}

		var dragStartY = 0;
		var dragY = 0;
		var dragging = false;
		var dragStartTime = 0;
		var handle = block.querySelector( '.offbeat-single-post-meta__sheet-handle' );
		var head = block.querySelector( '.offbeat-single-post-meta__sheet-head' );

		function onDragStart( e ) {
			if ( ! sheet.classList.contains( 'is-open' ) ) {
				return;
			}
			dragging = true;
			dragStartY = e.touches[ 0 ].clientY;
			dragY = 0;
			dragStartTime = e.timeStamp;
			sheet.style.transition = 'none';
		}

		function onDragMove( e ) {
			if ( ! dragging ) {
				return;
			}
			if ( e.cancelable ) {
				e.preventDefault();
			}
			dragY = e.touches[ 0 ].clientY - dragStartY;
			if ( dragY < 0 ) {
				dragY = 0;
			}
			sheet.style.transform = 'translateY(' + dragY + 'px)';
		}

		function onDragEnd( e ) {
			if ( ! dragging ) {
				return;
			}
			dragging = false;
			sheet.style.transition = '';
			var elapsed = e.timeStamp - dragStartTime;
			var velocity = dragY / ( elapsed || 1 );
			if ( dragY > sheet.offsetHeight * 0.35 || velocity > 0.6 ) {
				close();
			} else {
				sheet.style.transform = '';
			}
		}

		if ( handle ) {
			handle.addEventListener( 'touchstart', onDragStart, { passive: true } );
		}
		if ( head ) {
			head.addEventListener( 'touchstart', onDragStart, { passive: true } );
		}
		sheet.addEventListener( 'touchmove', onDragMove, { passive: false } );
		sheet.addEventListener( 'touchend', onDragEnd );

		scrim.addEventListener( 'click', close );
		closeBtn.addEventListener( 'click', close );
		window.addEventListener( 'keydown', function ( e ) {
			if ( e.key === 'Escape' && current !== null ) {
				close();
			}
		} );

		return {
			toggle: function ( toggle, target ) {
				if ( current === target ) {
					close();
				} else {
					open( toggle, target );
				}
			},
		};
	}

	function setupHoverCard( block ) {
		var card = block.querySelector( '.offbeat-single-post-meta__card' );
		var vendorPills = Array.prototype.slice.call(
			block.querySelectorAll( '.offbeat-single-post-meta__pill--vendor' )
		);
		if ( ! card || ! vendorPills.length ) {
			return;
		}

		var inner = card.querySelector( '.offbeat-single-post-meta__card-inner' );
		var thumb = card.querySelector( '.offbeat-single-post-meta__card-thumb' );
		var category = card.querySelector( '.offbeat-single-post-meta__card-category' );
		var name = card.querySelector( '.offbeat-single-post-meta__card-name' );
		var city = card.querySelector( '.offbeat-single-post-meta__card-city' );
		var locationMore = card.querySelector( '.offbeat-single-post-meta__card-location-more' );
		var excerpt = card.querySelector( '.offbeat-single-post-meta__card-excerpt' );
		var cta = card.querySelector( '.offbeat-single-post-meta__card-cta' );
		var arrow = card.querySelector( '.offbeat-single-post-meta__card-arrow' );
		var cardWidth = 274;
		var openTimer = null;
		var closeTimer = null;
		var isOpen = false;

		function fill( pill ) {
			var image = pill.getAttribute( 'data-image' );
			category.textContent = pill.getAttribute( 'data-category' ) || '';
			name.textContent = pill.getAttribute( 'data-name' ) || '';
			name.setAttribute( 'href', pill.getAttribute( 'href' ) || '#' );
			city.textContent = pill.getAttribute( 'data-location' ) || '';
			var more = pill.getAttribute( 'data-location-more' ) || '';
			locationMore.textContent = more ? ' ' + more : '';
			excerpt.textContent = pill.getAttribute( 'data-excerpt' ) || '';
			cta.setAttribute( 'href', pill.getAttribute( 'href' ) || '#' );

			if ( image ) {
				card.classList.remove( 'offbeat-single-post-meta__card--no-image' );
				thumb.style.backgroundImage = 'url(' + image + ')';
				thumb.textContent = '';
			} else {
				card.classList.add( 'offbeat-single-post-meta__card--no-image' );
				thumb.style.backgroundImage = 'none';
				thumb.textContent = pill.getAttribute( 'data-initial' ) || '';
			}
		}

		function position( pill ) {
			var blockRect = block.getBoundingClientRect();
			var pillRect = pill.getBoundingClientRect();
			var pillCenter = pillRect.left - blockRect.left + pillRect.width / 2;
			var pillTop = pillRect.top - blockRect.top;

			var left = pillCenter - cardWidth / 2;
			var min = 12;
			var max = blockRect.width - 12 - cardWidth;
			if ( max < min ) {
				max = min;
			}
			if ( left < min ) {
				left = min;
			}
			if ( left > max ) {
				left = max;
			}

			var arrowLeft = pillCenter - left;
			if ( arrowLeft < 20 ) {
				arrowLeft = 20;
			}
			if ( arrowLeft > cardWidth - 20 ) {
				arrowLeft = cardWidth - 20;
			}

			card.style.left = left + 'px';
			card.style.top = ( pillTop - card.offsetHeight ) + 'px';
			arrow.style.left = ( arrowLeft - 9 ) + 'px';
		}

		function reveal( pill ) {
			fill( pill );
			card.style.visibility = 'hidden';
			card.classList.add( 'is-visible' );
			position( pill );
			card.style.visibility = '';
			isOpen = true;
			card.setAttribute( 'aria-hidden', 'false' );
		}

		function open( pill ) {
			clearTimeout( closeTimer );
			if ( isOpen ) {
				reveal( pill );
				return;
			}
			clearTimeout( openTimer );
			openTimer = setTimeout( function () {
				reveal( pill );
			}, 120 );
		}

		function scheduleClose() {
			clearTimeout( openTimer );
			clearTimeout( closeTimer );
			closeTimer = setTimeout( function () {
				card.classList.remove( 'is-visible' );
				isOpen = false;
				card.setAttribute( 'aria-hidden', 'true' );
			}, 80 );
		}

		vendorPills.forEach( function ( pill ) {
			pill.addEventListener( 'mouseenter', function () {
				open( pill );
			} );
			pill.addEventListener( 'focus', function () {
				open( pill );
			} );
			pill.addEventListener( 'mouseleave', scheduleClose );
			pill.addEventListener( 'blur', scheduleClose );
		} );

		if ( inner ) {
			card.addEventListener( 'mouseenter', function () {
				clearTimeout( closeTimer );
			} );
			card.addEventListener( 'mouseleave', scheduleClose );
		}
	}
} );
